import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Form, Label, Input, Button } from '@shared/components';
import { validateVinVrm } from '../../utils/vinVrmValidation';
import type { VinVrmSearchInput } from '../../types/vinvrmsearch.types';

interface VinVrmSearchFormProps {
    onSearch: (vinVrm: VinVrmSearchInput) => void;
    initialValue?: string;
    isLoading?: boolean;
    isSearchLoading?: boolean;
    error?: string;
}

export const VinVrmSearchForm: React.FC<VinVrmSearchFormProps> = ({
    onSearch,
    initialValue = '',
    isLoading = false,
    isSearchLoading = false,
    error,
}) => {
    const [vinVrm, setVinVrm] = useState(initialValue);
    const [validationError, setValidationError] = useState<string>('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const cleanInput = vinVrm.trim().replace(/\s/g, '').toUpperCase();
        if (!cleanInput) {
            setValidationError('Please enter a VIN or Registration number');
            return;
        }

        // Validate input before submitting
        const validation = validateVinVrm(vinVrm);
        if (!validation.isValid) {
            setValidationError(validation.error || 'Invalid format');
            return;
        }

        setValidationError('');
        // Prepare search parameters
        const searchParams: VinVrmSearchInput = {};
        if (validation.type === 'vin') {
            searchParams.vin = cleanInput;
        } else if (validation.type === 'vrm') {
            searchParams.vrm = cleanInput;
        }
        onSearch(searchParams);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Allow alphanumeric characters and spaces, convert to uppercase
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
        setVinVrm(value);

        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError('');
        }
    };

    return (
        <div className="rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6">
            <div className="space-y-4 mb-6">
                <h3 className="text-lg font-semibold text-mp-foreground">
                    Search by Registration or VIN
                </h3>
                <p className="text-sm text-mp-muted">
                    Enter your vehicle's registration number or VIN for instant results
                </p>
            </div>

            <Form onSubmit={handleSubmit} className="flex flex-col space-y-4">
                {/* UK Registration Plate Style Input */}
                <div className="relative">
                    <Label htmlFor="vinvrm" className="sr-only">
                        Vehicle Registration or VIN
                    </Label>

                    {/* UK Reg Plate Styled Container */}
                    <Input
                        id="vinvrm"
                        value={vinVrm}
                        onChange={handleInputChange}
                        placeholder="UK Reg (e.g., AB12 CDE) or VIN"
                        max="17" // VINs are 17 characters max
                        disabled={isLoading}
                        error={!!validationError}
                        hint={validationError}
                    />
                </div>
                <div>
                    {/* Search Button */}
                    <Button
                        disabled={!vinVrm.trim() || isLoading || isSearchLoading}
                        startIcon={<Search size={18} />}
                        className="w-full"
                        size="sm"
                    >
                        {isSearchLoading ? 'Searching...' : 'Search Vehicle'}
                    </Button>
                </div>
                {error && <p className="text-sm text-center text-mp-error">{error}</p>}

                {/* Help Text */}

                <p className="text-xs text-mp-muted">
                    Enter a UK registration number (e.g., AB12 CDE) or 17-character VIN
                </p>
            </Form>
        </div>
    );
};
